import { useQuery } from 'react-query';
import { api } from './useAuth';

export interface PortalBranch {
  id: number;
  name: string;
  nameAr?: string;
}

export interface PortalRoom {
  id: number;
  name: string;
  nameAr?: string;
  branchId: number;
  minPlayers?: number;
  maxPlayers?: number;
}

export interface PortalSlot {
  startTime: string;
  endTime: string;
  eventId: string;
  price?: { amount: string; currency: string } | null;
}

export interface BookingSearchResult {
  id: number;
  bookingRef?: string;
  branchId: number;
  branchName?: string;
  roomId: number;
  roomName?: string;
  slotStartAt: string;
  players?: number;
  customerName?: string;
  customerPhone?: string;
  preferredLanguage?: string;
  status?: string;
}

export const usePortalBranches = () =>
  useQuery(
    ['portal-branches'],
    async () => {
      const { data } = await api.get('/portal-data/branches');
      return (data?.branches ?? []) as PortalBranch[];
    },
    { staleTime: 5 * 60_000 }
  );

export const usePortalRooms = (branchId?: number) =>
  useQuery(
    ['portal-rooms', branchId],
    async () => {
      const { data } = await api.get(`/portal-data/rooms?branchId=${branchId}`);
      return (data?.rooms ?? []) as PortalRoom[];
    },
    { enabled: !!branchId && branchId > 0, staleTime: 5 * 60_000 }
  );

export const usePortalSlots = (roomId?: number, date?: string, players = 2) =>
  useQuery(
    ['portal-slots', roomId, date, players],
    async () => {
      const { data } = await api.get(
        `/portal-data/slots?roomId=${roomId}&date=${encodeURIComponent(date || '')}&players=${players}`
      );
      return (data?.slots ?? []) as PortalSlot[];
    },
    { enabled: !!roomId && !!date, staleTime: 60_000 }
  );

export const useBookingSearch = (
  params: { bookingRef?: string; phone?: string; branchId?: number; date?: string },
  enabled = false
) =>
  useQuery(
    ['portal-bookings', params],
    async () => {
      const qs = new URLSearchParams();
      if (params.bookingRef) qs.set('ref', params.bookingRef);
      if (params.phone) qs.set('phone', params.phone);
      if (params.branchId) qs.set('branchId', String(params.branchId));
      if (params.date) qs.set('date', params.date);
      const { data } = await api.get(`/portal-data/bookings/search?${qs}`);
      return (data?.bookings ?? []) as BookingSearchResult[];
    },
    { enabled, staleTime: 0 }
  );
